import { Link } from "react-router-dom";
import type { EducationCourse } from "@platform/billing";
import { LuChevronRight } from "@platform/icon";
import { PATHS } from "@/shared/constants/app/routes.constant.ts";

/**
 * 과정 카드. 주 동선은 **회차 선택**이다.
 *
 * `회차 선택` 버튼의 `::after` 를 카드 전면에 깔아 카드 어디를 눌러도 같은 동작이 되게
 * 한다 — 큰 카드에서 작은 버튼만 조준하게 만들지 않으면서, 실제 인터랙티브 요소는
 * 버튼 하나로 유지해 중첩 컨트롤(버튼 안의 링크)을 만들지 않는 방법이다.
 * 부 동선인 `과정 상세` 만 `z-10` 으로 그 위에 띄운다.
 */
export function CourseCard({
  course,
  onSelect,
}: {
  course: EducationCourse;
  onSelect?: (course: EducationCourse) => void;
}) {
  const openCount = course.openSessionCount ?? 0;

  return (
    <article className="group relative grid min-h-36 grid-cols-[7rem_minmax(0,1fr)] overflow-hidden rounded-lg border border-gray-200 bg-white transition-colors hover:border-brand-500 has-[button:focus-visible]:border-brand-500 dark:border-white/15 dark:bg-white/[0.02]">
      {course.thumbnailUrl ? (
        <img
          src={course.thumbnailUrl}
          alt=""
          className="h-full min-h-36 w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex min-h-36 items-center justify-center bg-gray-100 px-3 text-center text-xs font-semibold text-gray-400 dark:bg-white/5 dark:text-neutral-500">
          적산 교육
        </div>
      )}
      <div className="flex min-w-0 flex-col p-4">
        <div className="flex items-center gap-2">
          <span
            className={`text-xs font-medium ${
              openCount > 0
                ? "text-brand-600 dark:text-brand-300"
                : "text-gray-400 dark:text-neutral-500"
            }`}
          >
            {openCount > 0 ? `접수중 ${openCount}회차` : "접수 예정"}
          </span>
        </div>
        <h3 className="mt-2 font-semibold leading-snug text-gray-950 group-hover:text-brand-700 dark:text-neutral-100 dark:group-hover:text-brand-300">
          {course.title}
        </h3>
        {course.summary && (
          <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-neutral-400">
            {course.summary}
          </p>
        )}
        <div className="mt-auto flex items-center justify-between gap-3 pt-3">
          <Link
            to={PATHS.educationCourse(course.id)}
            className="relative z-10 text-xs font-semibold text-gray-500 underline-offset-4 hover:text-gray-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:text-neutral-400 dark:hover:text-white"
          >
            과정 상세
          </Link>
          {onSelect && (
            <button
              type="button"
              onClick={() => onSelect(course)}
              className="inline-flex items-center gap-1 rounded-md bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition-colors after:absolute after:inset-0 after:content-[''] hover:bg-brand-700 focus-visible:outline-none dark:bg-white dark:text-gray-950 dark:hover:bg-brand-200"
            >
              회차 선택
              <LuChevronRight
                aria-hidden
                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              />
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
